import type {
  UnitInfoBananaScreenInterface,
  UnitInfoBreakerInterface,
  UnitInfoCMScreenInterface,
  UnitInfoCrusherInterface,
  UnitInfoCrusherVSIInterface,
  UnitInfoFeedInterface,
  UnitInfoFeederScreenInterface,
  UnitInfoFreeFallScreenSFInterface,
  UnitInfoFreeFallScreenSSInterface,
  UnitInfoGenericCrusherInterface,
  UnitInfoGenericScreenInterface,
  UnitInfoGrizzlyFeederInterface,
  UnitInfoGrizzlyScreenSGInterface,
  UnitInfoGyratoryCrusherCGInterface,
  UnitInfoHybridCrusherInterface,
  UnitInfoImpactCrusherCIInterface,
  UnitInfoJawCrusherInterface,
  UnitInfoKwataniScreenInterface,
  UnitInfoLMScreenInterface,
  UnitInfoMultislopeScreenInterface,
  UnitInfoPanFeederInterface,
  UnitInfoPlateFeederInterface,
  UnitInfoSchenckScreenInterface,
  UnitInfoStorageInterface,
} from 'store/units/types';
import { UnitTypes } from 'unitData/types';
import {
  getCMDeckMap,
  getDeckMap,
  getFeederScreenDeckMap,
  getFreefallDecks,
  getMultislopeDeckMap,
} from './utils';
import { getScreenFamily as getCMScreenFamily } from 'unitData/CircularMotionScreen/CircularMotionScreenData';
import { getScreenFamily } from 'unitData/MultislopeScreen/MultislopeScreenData';

export const storageRequest = (unitInfo: UnitInfoStorageInterface) => ({
  unitInfo: {
    showOversize: unitInfo.showOversize,
    byProduct: unitInfo.byProduct,
  },
});

export const feedRequest = (unitInfo: UnitInfoFeedInterface) => ({
  unitInfo: {
    moistureUI: unitInfo.moistureUI,
    psd: unitInfo.inputFlow.psd,
    size: unitInfo.inputFlow.size,
  },
});

export const storageFeederRequest = (unitInfo: UnitInfoStorageInterface) => ({
  unitInfo: {
    splitType: unitInfo.feederOutputOptions,
    feedInOut: unitInfo.feedInOut,
    amountRight: unitInfo.amountRight,
    feederModelRight: unitInfo.rightPanFeederModelName,
    inclinationRight: unitInfo.inclinationRight,
    otherModelRight: unitInfo.otherModelRight,
    otherModelMaxCapacityRight: unitInfo.otherModelMaxCapacityRight,
    ulip: unitInfo.ulip,
    mining: unitInfo.mining,
    autoSelect: unitInfo.autoSelect,
    byProduct: unitInfo.byProduct,
    highBin: unitInfo.useAsHighBin,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
  },
});

export const storageFeederTwoOutputsRequest = (
  unitInfo: UnitInfoStorageInterface,
) => ({
  type: UnitTypes.STORAGE_FEEDER,
  unitInfo: {
    ...storageFeederRequest(unitInfo).unitInfo,
    amountLeft: unitInfo.amountLeft,
    feederModelLeft: unitInfo.leftPanFeederModelName,
    inclinationLeft: unitInfo.inclinationLeft,
    otherModelLeft: unitInfo.otherModelLeft,
    otherModelMaxCapacityLeft: unitInfo.otherModelMaxCapacityLeft,
  },
});

export const coneCrusherBaseUnitInfo = (
  unitInfo: UnitInfoCrusherInterface,
) => ({
  modelName: unitInfo.modelName,
  concave: unitInfo.concave,
  mantle: unitInfo.mantle,
  unitThrow: unitInfo.unitThrow,
  css: unitInfo.css,
  moisture: unitInfo.moisture,
  newMoisture: unitInfo.newMoisture,
  parallelUnits: unitInfo.parallelUnits,
});

export const coneCrusherCHRequest = (unitInfo: UnitInfoCrusherInterface) => ({
  unitInfo: {
    ...coneCrusherBaseUnitInfo(unitInfo),
    maxCSS: unitInfo.maxCSS,
    otherModelName:
      typeof unitInfo.otherModelName !== 'undefined' &&
      unitInfo.otherModelName.length > 0,
    // High bin props
    highBin: unitInfo.highBin.useHighBin,
    splitType: unitInfo.highBin.feederOutputOptions,
    feedInOut: unitInfo.highBin.feedInOut,
    amountRight: unitInfo.highBin.amountRight,
    feederModelRight: unitInfo.highBin.rightPanFeederModelName,
    inclinationRight: unitInfo.highBin.inclinationRight,
    otherModelRight: unitInfo.highBin.otherModelRight,
    otherModelMaxCapacityRight: unitInfo.highBin.otherModelMaxCapacityRight,
    mining: unitInfo.highBin.mining,
    ulip: unitInfo.highBin.ulip,
    autoSelect: unitInfo.highBin.autoSelect,
  },
});

export const coneCrusherCSRequest = (unitInfo: UnitInfoCrusherInterface) => ({
  unitInfo: {
    ...coneCrusherBaseUnitInfo(unitInfo),
    otherModel: typeof unitInfo.otherModelName !== 'undefined',
    binFeederUsed: false,
  },
});

export const jawCrusherRequest = (unitInfo: UnitInfoJawCrusherInterface) => ({
  unitInfo: {
    modelName: unitInfo.modelName,
    css: unitInfo.css,
    otherModel:
      typeof unitInfo.otherModelName !== 'undefined' &&
      unitInfo.otherModelName.length > 0,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture ?? 0,
    application: unitInfo.attachmentTool.application,
    selectBreaker: unitInfo.attachmentTool.enabled,
  },
});

export const hybridCrusherRequest = (
  unitInfo: UnitInfoHybridCrusherInterface,
) => ({
  unitInfo: {
    rollDiameter: unitInfo.rollDiameter,
    rollWidth: unitInfo.rollWidth,
    segment: unitInfo.segment,
    adaptedGapSetting: unitInfo.gapSetting,
    compressiveStrength: unitInfo.compressiveStrength,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
  },
});

export const gyratoryCrusherRequest = (
  unitInfo: UnitInfoGyratoryCrusherCGInterface,
) => ({
  unitInfo: {
    modelName: unitInfo.modelName,
    unitThrow: unitInfo.unitThrow,
    oss: unitInfo.oss,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture ?? 0,
    grizzly: unitInfo.grizzly,
    bypass: unitInfo.bypass,
    application: unitInfo.attachmentTool.application,
    selectBreaker: unitInfo.attachmentTool.enabled,
    decks: {
      '0': {
        separationSize: unitInfo.decks[0].separationSize,
      },
    },
  },
});

export const impactCrusherUnitInfoBase = (
  unitInfo: UnitInfoCrusherVSIInterface | UnitInfoImpactCrusherCIInterface,
) => ({
  modelName: unitInfo.modelName,
  otherModel: typeof unitInfo.otherModelName !== 'undefined',
  motorFrequency: unitInfo.motorFrequency,
  motorPower: unitInfo.motorPower,
  tipSpeed: unitInfo.tipSpeed,
  parallelUnits: unitInfo.parallelUnits,
  newMoisture: unitInfo.newMoisture,
  moisture: unitInfo.moisture ?? 0,
});

export const impactCrusherCVRequest = (
  unitInfo: UnitInfoCrusherVSIInterface,
) => ({
  unitInfo: {
    ...impactCrusherUnitInfoBase(unitInfo),
    materialShape: unitInfo.materialShape.value,
  },
});

export const impactCrusherCIRequest = (
  unitInfo: UnitInfoImpactCrusherCIInterface,
) => ({
  unitInfo: {
    ...impactCrusherUnitInfoBase(unitInfo),
    setting: unitInfo.setting,
  },
});

export const genericCrusherRequest = (
  unitInfo: UnitInfoGenericCrusherInterface,
) => ({
  unitInfo: {
    psd: unitInfo.percent,
    size: unitInfo.size,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
  },
});

export const bananaScreenRequest = (
  unitInfo: UnitInfoBananaScreenInterface,
) => ({
  unitInfo: {
    rockMaterial: unitInfo.materialType.value,
    wetScreening: unitInfo.wetScreening,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
    width: unitInfo.width,
    length: unitInfo.length,
    decks: getDeckMap(unitInfo.deckList, unitInfo.nbrOfDecks),
  },
});

export const schenckAndKwataniUnitInfoBase = (
  unitInfo: UnitInfoSchenckScreenInterface | UnitInfoKwataniScreenInterface,
) => ({
  modelName: unitInfo.modelName,
  rockMaterial: unitInfo.materialType.value,
  decks: getDeckMap(unitInfo.deckList, unitInfo.nbrOfDecks),
  wetScreening: unitInfo.wetScreening,
  parallelUnits: unitInfo.parallelUnits,
  newMoisture: unitInfo.newMoisture,
  moisture: unitInfo.moisture,
  width: unitInfo.width,
  length: unitInfo.length,
});

export const schenckScreenRequest = (
  unitInfo: UnitInfoSchenckScreenInterface,
) => ({
  unitInfo: { ...schenckAndKwataniUnitInfoBase(unitInfo), schenck: true },
  type: UnitTypes.BANANA_SCREEN,
});

export const kwataniScreenRequest = (
  unitInfo: UnitInfoKwataniScreenInterface,
) => ({
  type: UnitTypes.BANANA_SCREEN,
  unitInfo: {
    ...schenckAndKwataniUnitInfoBase(unitInfo),
    kwatani: true,
    weight: unitInfo.weight,
  },
});

export const cmScreenRequest = (
  unitInfo: UnitInfoCMScreenInterface,
  screenCalcMethod: string,
) => ({
  unitInfo: {
    screenFamily: getCMScreenFamily(unitInfo.modelName),
    screenModel: unitInfo.modelName,
    nbrOfDecks: unitInfo.nbrOfDecks,
    inclination: unitInfo.incline,
    decks: getCMDeckMap(unitInfo, unitInfo.nbrOfDecks, screenCalcMethod),
    deckOneRelieving: unitInfo.deckOneRelieving,
    materialType: unitInfo.materialType.value,
    wetScreening: unitInfo.wetScreening,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
  },
});

export const grizzlyScreenRequest = (
  unitInfo: UnitInfoGrizzlyScreenSGInterface,
) => {
  // Deck 1: Grizzly deck (deckList[0])
  const deck1 = {
    separationSize: unitInfo.deckList[0].separationSize,
    screeningMediaId: 'GRIZZLY',
  };

  // Deck 2: Separation deck (deckList[1]) - only if nbrOfDecks === 2
  const deck2 = unitInfo.deckList[1]
    ? {
        separationSize: unitInfo.deckList[1].separationSize,
        screeningMediaId: unitInfo.deckList[1].screeningElement.value,
        overUnderSize: unitInfo.deckList[1].maxAmountOfUnderSize.value,
        particleShape: unitInfo.deckList[1].particleShape.value,
        openArea: unitInfo.deckList[1].openArea,
        efficiencyParams: {
          mode: unitInfo.deckList[1].efficiency.value,
          value: unitInfo.deckList[1].efficiencyNumber,
        },
      }
    : undefined;

  return {
    unitInfo: {
      modelName: unitInfo.modelName,
      decks: unitInfo.nbrOfDecks === 1 ? { 0: deck1 } : { 0: deck1, 1: deck2 },
      materialType: unitInfo.materialType.value,
      parallelUnits: unitInfo.parallelUnits,
      newMoisture: unitInfo.newMoisture,
      moisture: unitInfo.moisture,
      separationRange: unitInfo.separationRange,
    },
  };
};

export const linearMotionScreenRequest = (
  unitInfo: UnitInfoLMScreenInterface,
  screenCalcMethod: string,
  _applicationType?: string,
) => ({
  type: 'CONVENTIONAL_SCREEN',
  // type:
  //   applicationType?.toUpperCase() === 'MINING'
  //     ? 'MULTISLOPE_SCREEN'
  //     : 'CONVENTIONAL_SCREEN',
  unitInfo: {
    screenFamily: unitInfo.screenFamily,
    screenModel: unitInfo.modelName,
    sizeOption: String(unitInfo.sizeOption),
    nbrOfDecks: unitInfo.nbrOfDecks,
    inclination: unitInfo.incline,
    decks: getCMDeckMap(
      unitInfo as unknown as UnitInfoCMScreenInterface,
      unitInfo.nbrOfDecks,
      screenCalcMethod,
    ),
    deckOneRelieving: unitInfo.deckOneRelieving,
    materialType: unitInfo.materialType.value,
    wetScreening: unitInfo.wetScreening,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
  },
});

export const multislopeScreenRequest = (
  unitInfo: UnitInfoMultislopeScreenInterface,
  screenCalcMethod: string,
) => ({
  type: 'MULTISLOPE_SCREEN',
  unitInfo: {
    screenFamily: getScreenFamily(),
    screenModel: unitInfo.modelName,
    sizeOption: String(unitInfo.sizeOption),
    nbrOfDecks: unitInfo.nbrOfDecks,
    materialType: unitInfo.materialType.value,
    inclination: unitInfo.incline,
    decks: getMultislopeDeckMap(
      unitInfo,
      unitInfo.nbrOfDecks,
      screenCalcMethod,
    ),
    wetScreening: unitInfo.wetScreening,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
  },
});

export const freefallSFScreenRequest = (
  unitInfo: UnitInfoFreeFallScreenSFInterface,
) => ({
  unitInfo: {
    modelName: unitInfo.modelName,
    materialType: unitInfo.materialType.value,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
    deckOneRelieving: unitInfo.deckOneRelieving,
    decks: getFreefallDecks(unitInfo.deckList, unitInfo.nbrOfDecks),
  },
});

export const freefallSSScreenRequest = (
  unitInfo: UnitInfoFreeFallScreenSSInterface,
) => ({
  unitInfo: {
    modelName: unitInfo.modelName,
    materialType: unitInfo.materialType.value,
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
    deckOneRelieving: unitInfo.deckOneRelieving,
    deckTwoRelieving: unitInfo.deckTwoRelieving,
    decks: getFreefallDecks(unitInfo.deckList, unitInfo.nbrOfDecks),
  },
});

export const feederAndScreenRequest = (
  unitInfo: UnitInfoFeederScreenInterface,
) => ({
  unitInfo: {
    modelName: unitInfo.modelName,
    noOfDecks: unitInfo.decks.length,
    decks: getFeederScreenDeckMap(unitInfo),
    parallelUnits: unitInfo.parallelUnits,
    newMoisture: unitInfo.newMoisture,
    moisture: unitInfo.moisture,
  },
});

export const genericScreenRequest = (
  unitInfo: UnitInfoGenericScreenInterface,
) => ({
  unitInfo: {
    noOfDecks: unitInfo.nbrOfDecks,
    decks: Object.values(unitInfo.deckList).reduce((obj, sep, i) => {
      if (i < unitInfo.nbrOfDecks) {
        return { ...obj, [i]: sep };
      }
      return obj;
    }, {}),
  },
});

export const grizzlyFeederRequest = (
  unitInfo: UnitInfoGrizzlyFeederInterface,
) => {
  let decks = {};
  if (unitInfo.nbrOfOutputs === 2) {
    decks = { 0: unitInfo.decks[0] };
  } else if (unitInfo.nbrOfOutputs === 3) {
    decks = {
      0: unitInfo.decks[0],
      1: unitInfo.decks[1],
    };
  }
  const cleanedunitInfo = {
    modelName: unitInfo.modelName,
    moisture: unitInfo.moisture,
    newMoisture: unitInfo.newMoisture,
    inclination: unitInfo.inclination,
    decks,
    parallelUnits: unitInfo.parallelUnits,
    otherModel: unitInfo.otherModel,
    otherModelMaxCapacity: unitInfo.otherModelNominalCapacity,
    otherModelMaxFeed: unitInfo.otherModelMaxFeedSize,
  };

  return { type: UnitTypes.GRIZZLY_FEEDER_GENERAL, unitInfo: cleanedunitInfo };
};

export const feederRequest = (
  unitInfo: UnitInfoPanFeederInterface | UnitInfoPlateFeederInterface,
) => ({ unitInfo });

export const getBreakerRequest = (unitInfo: UnitInfoBreakerInterface) => ({
  unitInfo: {
    application: unitInfo.attachmentTool.application,
  },
});
